import { NOTIFICATION_BATCH_SIZE, splitRecipients } from "./notificationBatches";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Alert, Button, Input, Label, Table } from "../../../Components/ui";
import { notificationCenterService, NotificationRecipient, NotificationRecipientQuery } from "../../../api/services/notification-center.service";

export const recipientPeriod = (months: number, now = new Date()) => {
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  const [year, month, day] = today.split("-").map(Number);
  const start = new Date(Date.UTC(year, month - 1 - months, 1));
  const lastDay = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0)).getUTCDate();
  start.setUTCDate(Math.min(day, lastDay));
  return { start_date: start.toISOString().slice(0, 10), end_date: today };
};

type Props = { value: number[]; onChange: (ids: number[]) => void; disabled?: boolean };
export default function NotificationRecipientSelector({ value, onChange, disabled = false }: Props) {
  const [mode, setMode] = useState<NotificationRecipientQuery["mode"]>("individual");
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("1");
  const [dates, setDates] = useState(() => recipientPeriod(1));
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<NotificationRecipient[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const generation = useRef(0);
  const selectionRef = useRef(value);
  selectionRef.current = value;
  const query: NotificationRecipientQuery = { mode, search, page, page_size: 50, ...(mode === "individual" ? {} : dates) };
  const queryKey = JSON.stringify(query);
  const invalidate = () => { generation.current += 1; setLoading(true); setSelecting(false); setPage(1); };
  useEffect(() => {
    const version = ++generation.current;
    let active = true;
    setLoading(true);
    setError("");
    const timer = setTimeout(() => {
      void notificationCenterService.getRecipients(JSON.parse(queryKey))
        .then(({ data }) => { if (active && version === generation.current) { setRows(data.results); setCount(data.count); } })
        .catch(() => { if (active && version === generation.current) { setRows([]); setError("고객을 불러오지 못했습니다."); } })
        .finally(() => { if (active && version === generation.current) setLoading(false); });
    }, 250);
    return () => { active = false; generation.current += 1; clearTimeout(timer); };
  }, [queryKey, refresh]);

  const update = (ids: number[]) => {
    const unique = [...new Set(ids)];
    onChange(unique);
  };
  const selectAll = async () => {
    const version = generation.current;
    setSelecting(true);
    try {
      const { data } = await notificationCenterService.selectAllRecipients(query);
      if (version === generation.current) update([...selectionRef.current, ...data.recipient_ids]);
    } catch (err: any) {
      if (version === generation.current) toast.error(err?.response?.data?.detail || "고객 전체 선택에 실패했습니다.");
    } finally { if (version === generation.current) setSelecting(false); }
  };
  const selected = new Set(value);
  return <fieldset disabled={disabled} className="border rounded p-3 mb-3">
    <legend className="fs-6 float-none w-auto px-1">고객 선택</legend>
    <div className="d-flex flex-wrap gap-2 mb-3">
      <Label htmlFor="recipient-mode" className="visually-hidden">고객 조회 방식</Label>
      <Input id="recipient-mode" type="select" value={mode} onChange={(e) => { invalidate(); setMode(e.target.value as typeof mode); }} style={{ maxWidth: 200 }}>
        <option value="individual">개별선택 (고객 검색)</option><option value="recent_access">최근 접속 고객</option><option value="recent_purchase">최근 구매 고객</option>
      </Input>
      <Label htmlFor="recipient-search" className="visually-hidden">고객 검색</Label>
      <Input id="recipient-search" value={search} onChange={(e) => { invalidate(); setSearch(e.target.value); }} placeholder="고객명 또는 전화번호" style={{ maxWidth: 280 }} />
      {mode !== "individual" ? <>
        <Label htmlFor="recipient-period" className="visually-hidden">조회 기간</Label>
        <Input id="recipient-period" type="select" value={period} onChange={(e) => { invalidate(); setPeriod(e.target.value); if (e.target.value !== "custom") setDates(recipientPeriod(Number(e.target.value))); }} style={{ maxWidth: 130 }}>
          <option value="1">1개월</option><option value="3">3개월</option><option value="6">6개월</option><option value="custom">직접 설정</option>
        </Input>
        {period === "custom" ? <>
          <Input aria-label="조회 시작일" type="date" value={dates.start_date} onChange={(e) => { invalidate(); setDates({ ...dates, start_date: e.target.value }); }} style={{ maxWidth: 170 }} />
          <Input aria-label="조회 종료일" type="date" value={dates.end_date} onChange={(e) => { invalidate(); setDates({ ...dates, end_date: e.target.value }); }} style={{ maxWidth: 170 }} />
        </> : null}
      </> : null}
    </div>
    {mode === "recent_purchase" ? <p className="small text-muted">주문일 기준 · 유상 예약상품 구매 고객</p> : null}
    {mode === "recent_access" ? <p className="small text-muted">기록된 고객 웹 방문 기준</p> : null}
    <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
      <span>선택 {value.length.toLocaleString()}명</span>
      <Button size="sm" color="soft-primary" disabled={loading || selecting || Boolean(error) || !count} onClick={() => void selectAll()}>{selecting ? "선택 중" : `검색 결과 전체 선택 (${count.toLocaleString()}명)`}</Button>
      <Button size="sm" color="secondary" disabled={selecting || !value.length} onClick={() => update([])}>선택 전체 해제</Button>
    </div>
    {value.length >= NOTIFICATION_BATCH_SIZE ? <Alert color="info">{NOTIFICATION_BATCH_SIZE.toLocaleString()}명 단위로 자동 분할 발송됩니다. 현재 선택: {splitRecipients(value).length.toLocaleString()}개 묶음.</Alert> : null}
    {error ? <Alert color="danger">{error} <Button size="sm" onClick={() => setRefresh((x) => x + 1)}>다시 조회</Button></Alert> : null}
    <div className="table-responsive" style={{ maxHeight: 320 }}><Table className="small align-middle mb-0">
      <thead><tr><th>선택</th><th>고객명</th><th>전화번호</th>{mode !== "individual" ? <th>{mode === "recent_purchase" ? "주문일" : "접속일"}</th> : null}</tr></thead>
      <tbody>{loading ? <tr><td colSpan={4}>고객 조회 중...</td></tr> : !error && !rows.length ? <tr><td colSpan={4}>조회된 고객이 없습니다.</td></tr> : null}
        {!loading && rows.map((row) => <tr key={row.id}><td><Input type="checkbox" aria-label={`${row.name} 선택`} checked={selected.has(row.id)} disabled={selecting} onChange={() => update(selected.has(row.id) ? value.filter((id) => id !== row.id) : [...value, row.id])} /></td><td>{row.name}</td><td>{row.phone_num || "-"}</td>{mode !== "individual" ? <td>{row.last_activity_at?.slice(0, 10) || "-"}</td> : null}</tr>)}
      </tbody></Table></div>
    <div className="d-flex gap-2 align-items-center mt-2"><Button size="sm" disabled={loading || selecting || page <= 1} onClick={() => { generation.current += 1; setPage(page - 1); }}>이전</Button><span>{page} / {Math.max(1, Math.ceil(count / 50))}</span><Button size="sm" disabled={loading || selecting || page * 50 >= count} onClick={() => { generation.current += 1; setPage(page + 1); }}>다음</Button></div>
  </fieldset>;
}
